import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import API_URL from "../config";

const FeedbackModal = () => {
    const location = useLocation();
    const [shouldShow, setShouldShow] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    
    // Form State
    const [formData, setFormData] = useState({
        announcement_source: "",
        miss_announcements: "",
        communication_issue: "",
        teammate_source: "",
        teammate_difficulty: "",
        team_challenges: "",
        attendance_method: "",
        attendance_efficiency: "",
        interaction_level: "",
        needed_feature: "",
        feature_helpfulness: ""
    });

    const [userEmail, setUserEmail] = useState("");

    useEffect(() => {
        // Do not display on login screen
        if (location.pathname === "/") return;

        const checkEligibility = async () => {
            try {
                const storedUser = localStorage.getItem("vjconnect_user");
                if (!storedUser) return;
                
                const user = JSON.parse(storedUser);
                if (!user.email) return;

                setUserEmail(user.email);

                const res = await fetch(`${API_URL}/feedback/should-show?email=${encodeURIComponent(user.email)}`);
                if (!res.ok) return;

                const data = await res.json();
                if (data.shouldShow) {
                    setShouldShow(true);
                }
            } catch (err) {
                console.error("Error checking feedback eligibility:", err);
            }
        };

        checkEligibility();
    }, [location.pathname]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const isFormComplete = Object.values(formData).every((val) => val !== "");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormComplete) return;

        setIsSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/feedback/submit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: userEmail, ...formData }),
            });

            if (res.ok) {
                setSubmitted(true);
                setTimeout(() => setShouldShow(false), 2500);
            } else {
                alert("Failed to submit feedback. Please try again.");
            }
        } catch (err) {
            console.error("Error submitting feedback:", err);
            alert("Network error. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!shouldShow) return null;

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                {submitted ? (
                    <div style={styles.successMessage}>
                        <h2>Thank you for your response 🙌</h2>
                    </div>
                ) : (
                    <>
                        <h2 style={styles.title}>🚀 Student Connectivity Survey</h2>
                        <p style={styles.subtitle}>Help us improve communication, team formation, and attendance tracking!</p>
                        
                        <div style={styles.formContainer}>
                            <form onSubmit={handleSubmit}>
                                {questions.map((q, index) => (
                                    <div key={q.name} style={styles.questionBlock}>
                                        <label style={styles.questionLabel}>
                                            {index + 1}. {q.label}
                                        </label>
                                        <div style={styles.optionsGrid}>
                                            {q.options.map((opt) => (
                                                <label key={opt} style={styles.radioLabel}>
                                                    <input
                                                        type="radio"
                                                        name={q.name}
                                                        value={opt}
                                                        checked={formData[q.name] === opt}
                                                        onChange={handleChange}
                                                        style={styles.radioInput}
                                                    />
                                                    <span style={styles.radioText}>{opt}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                <button
                                    type="submit"
                                    style={{
                                        ...styles.submitButton,
                                        opacity: isFormComplete ? 1 : 0.5,
                                        cursor: isFormComplete ? "pointer" : "not-allowed"
                                    }}
                                    disabled={!isFormComplete || isSubmitting}
                                >
                                    {isSubmitting ? "Submitting..." : "Submit Feedback"}
                                </button>
                            </form>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const questions = [
    {
        name: "announcement_source",
        label: "How do you currently receive important college announcements?",
        options: ["WhatsApp groups", "Class Representatives", "Notice boards", "Faculty"]
    },
    {
        name: "miss_announcements",
        label: "Do you sometimes miss important announcements?",
        options: ["Very Often", "Sometimes", "Rarely", "Never"]
    },
    {
        name: "communication_issue",
        label: "Biggest issue with current communication?",
        options: [
            "Too many messages / information overload",
            "Important updates get buried",
            "Not part of all relevant groups",
            "Delayed or incorrect information"
        ]
    },
    {
        name: "teammate_source",
        label: "How do you find teammates?",
        options: ["Friends", "Classmates", "WhatsApp groups", "Assigned by faculty"]
    },
    {
        name: "teammate_difficulty",
        label: "Difficulty in finding teammates?",
        options: ["Very difficult", "Somewhat difficult", "Easy", "Very easy"]
    },
    {
        name: "team_challenges",
        label: "Challenges in forming teams?",
        options: [
            "Finding skilled people",
            "Lack of communication",
            "Limited network",
            "Time constraints"
        ]
    },
    {
        name: "attendance_method",
        label: "Attendance / permission handling?",
        options: ["Manual", "WhatsApp", "Class representative", "Online system"]
    },
    {
        name: "attendance_efficiency",
        label: "Efficiency of attendance process?",
        options: ["Very inefficient", "Somewhat inefficient", "Efficient", "Very efficient"]
    },
    {
        name: "interaction_level",
        label: "Interaction beyond friend circle?",
        options: ["Never", "Occasionally", "Sometimes", "Often", "Always"]
    },
    {
        name: "needed_feature",
        label: "Most needed feature?",
        options: [
            "Centralized announcements",
            "Team formation system",
            "Attendance tracking",
            "Networking"
        ]
    },
    {
        name: "feature_helpfulness",
        label: "If all these features are implemented, how easy will your tasks become?",
        options: ["★☆☆☆☆", "★★☆☆☆", "★★★☆☆", "★★★★☆", "★★★★★"]
    }
];

const styles = {
    overlay: {
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "20px"
    },
    modal: {
        backgroundColor: "var(--bg-color, #1a1b2e)",
        borderRadius: "16px",
        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
        width: "100%",
        maxWidth: "600px",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        border: "1px solid rgba(255, 255, 255, 0.1)"
    },
    successMessage: {
        padding: "60px 20px",
        textAlign: "center",
        color: "var(--text-color, #ffffff)"
    },
    title: {
        fontSize: "24px",
        fontWeight: "bold",
        padding: "20px 24px 8px",
        margin: 0,
        color: "var(--text-color, #ffffff)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.05)"
    },
    subtitle: {
        margin: "0",
        padding: "0 24px 16px",
        fontSize: "14px",
        color: "var(--text-secondary, #aaa)"
    },
    formContainer: {
        padding: "16px 24px 24px",
        overflowY: "auto",
        flex: 1
    },
    questionBlock: {
        marginBottom: "24px",
        background: "rgba(255, 255, 255, 0.03)",
        padding: "16px",
        borderRadius: "12px",
        border: "1px solid rgba(255, 255, 255, 0.05)"
    },
    questionLabel: {
        display: "block",
        fontSize: "15px",
        fontWeight: "600",
        marginBottom: "12px",
        color: "var(--text-color, #f0f0f0)"
    },
    optionsGrid: {
        display: "grid",
        gap: "10px",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))"
    },
    radioLabel: {
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        padding: "10px 14px",
        borderRadius: "8px",
        background: "rgba(255, 255, 255, 0.04)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        transition: "all 0.2s"
    },
    radioInput: {
        marginRight: "10px",
        accentColor: "#6B4EE6",
        transform: "scale(1.1)"
    },
    radioText: {
        fontSize: "14px",
        color: "var(--text-color, #e0e0e0)",
        lineHeight: "1.3"
    },
    submitButton: {
        width: "100%",
        padding: "14px",
        backgroundColor: "#6B4EE6",
        color: "white",
        border: "none",
        borderRadius: "8px",
        fontWeight: "bold",
        fontSize: "16px",
        marginTop: "10px",
        transition: "opacity 0.2s"
    }
};

export default FeedbackModal;
